import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { emailValidation } from '@adornis/users/db/validators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import { LogistikClearanceEntry, LogistikSettings } from '@chancenwerk/db/LogistikClearanceSettings.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { firstValueFrom, timer } from 'rxjs';

/**
 * @customElement logistik-page-options
 */
@customElement('logistik-page-options')
export class LogistikPageOptions extends ChemistryLitElement {
  @state() private readonly _settings = new RXController(
    this,
    LogistikSettings.get()(selectionSet(() => LogistikSettings, 2)),
  );
  @state() private readonly _form = new FormController(this, this._settings.observable, {
    onSuccess: async setting => {
      setting.frequentOrdersNotificationEmails?.forEach(email => {
        if (!email)
          throw new ValidationError('Meldungen bei häufigen Bestellungen E-Mail Adresse darf nicht leer sein');
        emailValidation({ value: email, key: 'frequestOrderEmail', target: this });
      });
    },
    success: async setting => {
      try {
        await ChromiumDialog.waitFor(Promise.all([setting.save(), firstValueFrom(timer(350))]));
        return void ChromiumSnackbar.show('Optionen erfolgreich aktualisiert!');
      } catch (e) {
        logger.error(e, 'Es gab ein Problem beim Speichern der Optionen');
        return ChromiumDialog.alert(
          'Es gab ein Problem beim Speichern der Optionen. Bitte versuchen Sie es später erneut!',
        );
      }
    },
  });

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle=${'Optionen für CHANCENlogistik'}
          backRoute=${LogistikRoutes.landing}
        ></logistik-component-base-header>
        <x-flex space="lg">
          ${this._clearanceSettings()} ${this._noMaxOrderAmountSettings()} ${this._haeufigenBestellungenSettings()}
          ${this._selfPrintNotificationSettings()} ${this._maxOrderAmountSupportEmail()}
          <x-flex horizontal end space="sm">
            <cr-button @click=${async (e: MouseEvent) => this._form.validate({ mouseEvent: e })}> Speichern </cr-button>
          </x-flex>
        </x-flex>
      </logistik-component-base-page-layout>
    `;
  }

  private _clearanceSettings() {
    return html`
      <x-flex space="md">
        <cr-h3> Freigabeoptionen für individuelle Bestellungen </cr-h3>
        <cr-text tone="subtle">
          Hier können die Freigabeoptionen für individuelle Bestellungen angepasst werden. Es kann festgelegt werden
          welche E-Mail ab welchem Betrag benachrichtigt wird.
        </cr-text>
        ${this._settings.isLoading
          ? Loader
          : this._settings.value
          ? html`
              <x-flex space="md">
                ${repeat(
                  this._settings.value.clearanceEntries,
                  (clearanceEntry, index) =>
                    html`
                      <x-flex horizontal crossaxis-center space="sm">
                        <cr-input
                          placeholder="Ab Betrag"
                          type="number"
                          postfix="€"
                          ${this._form.field('clearanceEntries', index, 'price')}
                        ></cr-input>
                        <cr-input
                          flex
                          placeholder="E-Mail"
                          ${this._form.field('clearanceEntries', index, 'email')}
                        ></cr-input>
                        <cr-button
                          mode="icon"
                          icon="delete"
                          tone="error"
                          @click=${() => {
                            this._settings.value!.clearanceEntries.splice(index, 1);
                            this.requestUpdate();
                          }}
                        ></cr-button>
                      </x-flex>
                    `,
                )}
                <cr-button
                  mode="outline"
                  icon="add"
                  @click=${() => {
                    this._settings.value?.clearanceEntries.push(new LogistikClearanceEntry({}));
                    this.requestUpdate();
                  }}
                >
                  Freigabestufe hinzufügen
                </cr-button>
              </x-flex>
            `
          : Nothing}
      </x-flex>
    `;
  }

  private _noMaxOrderAmountSettings() {
    return html`
      <x-flex space="md">
        <cr-h3> Freigabe für unbegrenzte Bestell-Anzahl </cr-h3>
        <cr-text tone="subtle">
          Hier können E-Mail-Adressen angegeben werden, die keine Maximal-Anzahl von einzelnen Produkten haben sollen.
        </cr-text>
        ${this._settings.isLoading
          ? Loader
          : this._settings.value
          ? html`
              <x-flex space="md">
                ${repeat(
                  this._settings.value.noMaxOrderAmountEmails,
                  (email, index) =>
                    html`
                      <x-flex horizontal crossaxis-center space="sm">
                        <cr-input
                          flex
                          placeholder="E-Mail Adresse"
                          ${this._form.field('noMaxOrderAmountEmails', index)}
                        ></cr-input>
                        <cr-button
                          mode="icon"
                          icon="delete"
                          tone="error"
                          @click=${() => {
                            this._settings.value!.noMaxOrderAmountEmails.splice(index, 1);
                            this.requestUpdate();
                          }}
                        ></cr-button>
                      </x-flex>
                    `,
                )}
                <cr-button
                  mode="outline"
                  icon="add"
                  @click=${() => {
                    this._settings.value?.noMaxOrderAmountEmails.push('');
                    this.requestUpdate();
                  }}
                >
                  E-Mail Adresse hinzufügen
                </cr-button>
              </x-flex>
            `
          : Nothing}
      </x-flex>
    `;
  }

  private _haeufigenBestellungenSettings() {
    return html`
      <x-flex space="md">
        <cr-h3> Meldungen bei häufigen Bestellungen </cr-h3>
        <cr-text tone="subtle">
          Hier können E-Mail-Adressen angegeben werden, die eine Benachrichtigung erhalten, wenn ein Kunde häufig
          Bestellungen aufgibt.
        </cr-text>
        ${this._settings.isLoading
          ? Loader
          : this._settings.value
          ? html`
              <x-flex space="md">
                ${repeat(
                  this._settings.value.frequentOrdersNotificationEmails ?? [],
                  (email, index) =>
                    html`
                      <x-flex horizontal crossaxis-center space="sm">
                        <cr-input
                          flex
                          placeholder="E-Mail Adresse"
                          ${this._form.field('frequentOrdersNotificationEmails', index)}
                        ></cr-input>
                        <cr-button
                          mode="icon"
                          icon="delete"
                          tone="error"
                          @click=${() => {
                            this._settings.value!.frequentOrdersNotificationEmails?.splice(index, 1);
                            this.requestUpdate();
                          }}
                        ></cr-button>
                      </x-flex>
                    `,
                )}
                <cr-button
                  mode="outline"
                  icon="add"
                  @click=${() => {
                    this._settings.value!.frequentOrdersNotificationEmails?.push('');
                    this.requestUpdate();
                  }}
                >
                  E-Mail Adresse hinzufügen
                </cr-button>
              </x-flex>
            `
          : Nothing}
      </x-flex>
    `;
  }

  private _selfPrintNotificationSettings() {
    return html`
      <cr-flex space="md">
        <cr-h3> Meldungen bei Selbstdruck-Bestellungen </cr-h3>
        <cr-text tone="subtle">
          Hier können E-Mail-Adressen angegeben werden, die eine Benachrichtigung erhalten, wenn ein CHANCENbrief zum
          Selbstdruck bestellt wird.
        </cr-text>
        ${this._settings.isLoading
          ? Loader
          : this._settings.value
          ? html`
              <cr-flex space="md">
                ${repeat(
                  this._settings.value.selfPrintNotificationEmails ?? [],
                  (email, index) => html`
                    <cr-flex horizontal crossaxis-center space="sm">
                      <cr-input
                        flex
                        placeholder="E-Mail Adresse"
                        ${this._form.field('selfPrintNotificationEmails', index)}
                      >
                      </cr-input>
                      <cr-button
                        mode="icon"
                        icon="delete"
                        tone="error"
                        @click=${() => {
                          this._settings.value!.selfPrintNotificationEmails?.splice(index, 1);
                          this.requestUpdate();
                        }}
                      >
                      </cr-button>
                    </cr-flex>
                  `,
                )}
                <cr-button
                  mode="outline"
                  icon="add"
                  @click=${() => {
                    if (!this._settings.value!.selfPrintNotificationEmails)
                      this._settings.value!.selfPrintNotificationEmails = [];
                    this._settings.value!.selfPrintNotificationEmails?.push('');
                    this.requestUpdate();
                  }}
                >
                  E-Mail Adresse hinzufügen
                </cr-button>
              </cr-flex>
            `
          : Nothing}
      </cr-flex>
    `;
  }

  private _maxOrderAmountSupportEmail() {
    return html`
      <cr-flex space="md">
        <cr-h3> Support E-Mail </cr-h3>
        <cr-text tone="subtle"> Support E-Mail </cr-text>
        ${this._settings.isLoading
          ? Loader
          : this._settings.value
          ? html`
              <cr-flex space="md">
                <cr-input
                  flex
                  placeholder="E-Mail Adresse"
                  ${this._form.field('maxOrderAmountSupportEmail')}
                ></cr-input>
              </cr-flex>
            `
          : Nothing}
      </cr-flex>
    `;
  }
}

const Loader = html` <x-flex h100 w100 flex center crossaxis-center> <cr-loader></cr-loader> </x-flex> `;
const Nothing = html`
  <x-empty-placeholder
    no-wrapper
    .text=${'Es gab ein Problem beim holen der Freigabeeinstellungen. Bitte versuchen Sie es später erneut!'}
    .img=${'close'}
  ></x-empty-placeholder>
`;
